import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	DashboardReports,
	ImageChartEntry,
	LeaderReportService,
	LeaderReportType,
	LeaderUserService,
	OverallScoresColumn,
	User,
} from '@services/leader';
import { AssessmentService, Organization } from '@services/member';
import {
	Domain,
	Label,
	LanguageService,
	MainKey,
	StorageService,
} from '@services/public';
import { Select, toVoidPromise } from '@util';
import { ChartConfiguration } from 'chart.js';
import { Chart } from 'chart.js/auto';
import { uuid } from 'jakapa-utilities-lite';
import { Observable, ReplaySubject, Subscription } from 'rxjs';

const PAGE_PATH = `pages.researcher.dashboard.measureDashboard`;

@Component({
	selector: 'app-leader-dashboard-measure',
	templateUrl: './measure.component.html',
	styleUrls: ['./measure.component.scss'],
})
export class LeaderDashboardMeasureComponent implements OnInit, OnDestroy {
	private readonly _key: symbol = Symbol();

	@ViewChild('growthCanvas', { read: ElementRef }) growthCanvas: ElementRef;

	// Assessment history.
	filteredAssessmentHistory: any;
	assessmentHistory: any;

	// Domain Overall Scores variables.
	isPieChartVisible: boolean = true;
	viewMoreDomainOverallScores: boolean;

	// Report data.
	domainOverallScores: Array<OverallScoresColumn> = [];
	growth: Chart = null;
	strengthsAndWeaknesses: Array<ImageChartEntry> = [];
	selfAwareness: Array<ImageChartEntry> = [];
	p: number = 1;

	// Reports.
	reports: DashboardReports;
	domainGrowthDomainFilter: Select<Domain>;
	selfByGroupFilters: Array<Select<any>> = [];
	selfByGroupChartConfiguration: ReplaySubject<ChartConfiguration> =
		new ReplaySubject<ChartConfiguration>();
	selfVsPeerByUserFilters: Array<Select<any>> = [];
	selfVsPeerByUserChartConfiguration: ReplaySubject<ChartConfiguration> =
		new ReplaySubject<ChartConfiguration>();

	// Local data storage from API calls.
	private _users: Array<User> | null = null;

	// Page subscriptions.
	private _subscriptions: Subscription = new Subscription();

	// Page language.
	page: { [key: string]: string } = {};
	labels: { [key in Label]?: string } = {
		[Label.UNKNOWN_USER]: '',
	};

	constructor(
		private _assessmentSvc: AssessmentService,
		private _languageSvc: LanguageService,
		private _reportSvc: LeaderReportService,
		private _storageSvc: StorageService,
		private _userSvc: LeaderUserService,
	) {}

	async ngOnInit(): Promise<void> {
		// Get page language.
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] !== 'object' || value[PAGE_PATH] === null)
				return;
			this.page = value[PAGE_PATH];
			for (const key in this.page)
				this._languageSvc
					.template(this.page[key])
					.then((value) => (this.page[key] = value));
		});
		this._languageSvc.getLabels(this.labels);

		// Get reports.
		this.reports = await this._reportSvc.getDashboardReports(this._key);

		// Set filter events.
		this._subscriptions.add(
			this.reports.filters.organization.onChange.subscribe(
				(organization: Organization) =>
					this._storageSvc.setMainStorage(
						MainKey.SELECTED_ORGANIZATION_UUID,
						organization.uuid,
					),
			),
		);

		// Subscribe to domain growth.
		this.domainGrowthDomainFilter =
			this.reports.reports[LeaderReportType.DOMAIN_GROWTH].filters.domain;
		this.reports.reports[LeaderReportType.DOMAIN_GROWTH].subscribe(
			this._key,
			(value: ChartConfiguration) => {
				if (!!this.growth) this.growth.destroy();
				if (this.growthCanvas) {
					this.growth = new Chart(this.growthCanvas.nativeElement, value);
				}
			},
		);

		// Subscribe to domain overall scores.
		this.reports.reports[LeaderReportType.DOMAIN_OVERALL_SCORES].subscribe(
			this._key,
			(value: Array<OverallScoresColumn>) => (this.domainOverallScores = value),
		);

		// Subscribe to self awareness.
		this.reports.reports[LeaderReportType.SELF_AWARENESS].subscribe(
			this._key,
			(value: Array<ImageChartEntry>) => (this.selfAwareness = value),
		);

		// Subscribe to self by group.
		const selfByGroup = this.reports.reports[LeaderReportType.SELF_BY_GROUP];
		this.selfByGroupFilters.push(
			selfByGroup.filters.domain,
			selfByGroup.filters.viewAll,
		);
		selfByGroup.subscribe(this._key, this.selfByGroupChartConfiguration);

		// Subscribe to self vs peer by user.
		const selfVsPeerByUser =
			this.reports.reports[LeaderReportType.SELF_VS_PEER_BY_USER];
		this.selfVsPeerByUserFilters.push(
			selfVsPeerByUser.filters.domain,
			selfVsPeerByUser.filters.viewAll,
		);
		selfVsPeerByUser.subscribe(
			this._key,
			this.selfVsPeerByUserChartConfiguration,
		);

		// Subscribe to strengths and weaknesses.
		this.reports.reports[LeaderReportType.STRENGTHS_AND_WEAKNESSES].subscribe(
			this._key,
			(value: Array<ImageChartEntry>) => (this.strengthsAndWeaknesses = value),
		);

		// Load users.
		const apiCall: Observable<Array<User>> = this._userSvc.getUsers();
		apiCall.subscribe((res: Array<User>) => (this._users = res));
		await toVoidPromise(apiCall);

		// Load assessment history.
		this._assessmentSvc.getAssessmentHistory().subscribe((res: any) => {
			if (!res) return;
			this.assessmentHistory = [
				...new Set(res.map((userRow: any) => userRow.uuid)),
			].map((uuid: uuid) => res.find((userRow: any) => userRow.uuid === uuid));
			this.assessmentHistory.forEach((userRow: any) => {
				userRow.name =
					this._users.find((user) => user.uuid === userRow.uuid)?.name ||
					this.labels[Label.UNKNOWN_USER];
			});

			// Set filter events.
			this._subscriptions.add(
				this.reports.filters.organization.onChange.subscribe(() =>
					setTimeout(() => this._filterAssessmentHistory()),
				),
			);
			this._subscriptions.add(
				this.reports.filters.group.onChange.subscribe(() =>
					setTimeout(() => this._filterAssessmentHistory()),
				),
			);

			// Initialize reports.
			this.reports.filters.organization.value =
				this.reports.filters.organization.options.find(
					(organization: Organization) =>
						organization.uuid ===
						this._storageSvc.getMainStorage(MainKey.SELECTED_ORGANIZATION_UUID),
				) || this.reports.filters.organization.value;
		});
	}

	private _filterAssessmentHistory() {
		this.filteredAssessmentHistory = this.assessmentHistory.filter(
			(assessment: any) =>
				this.reports.filters.user.options.some(
					(user) => user.uuid === assessment.uuid,
				),
		);
	}

	//pie chart
	pieChartLabel(series: any[], name: string): string {
		const item = series.filter((data) => data.name === name);
		if (item.length > 0) {
			return item[0].label;
		}
		return name;
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
		this._reportSvc.unsubscribe(this._key);
	}
}
